import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const extractApiErrorMessage = (data, fallback = "An unknown error occurred") => {
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
  if (Array.isArray(data.data)) {
    const fromData = data.data.find((item) => typeof item === "string" && item.trim());
    if (fromData) return fromData.trim();
  }
  if (data.errors && typeof data.errors === "object") {
    const flat = Object.values(data.errors).flat();
    const fromErrors = flat.find((item) => typeof item === "string" && item.trim());
    if (fromErrors) return fromErrors.trim();
  }
  return fallback;
};

// all drivers
export const fetchDrivers = createAsyncThunk(
  "drivers/fetchAll",
  async (
    {
      page = 1,
      per_page,
      id = "",
      name = "",
      search = "",
      email = "",
      service_type = "",
      carrier_type = "",
      plate_no = "",
      partner_id = "",
      account_type = "",
      city = "",
      status = "",
      availability = "",
      date_filter = "",
      date_value = "",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (per_page) params.set("per_page", String(per_page));
      // Text text must go as `search`, never as `id`.
      const searchTerm = String(search || name || "").trim();
      if (searchTerm) params.set("search", searchTerm);
      const filters = {
        id,
        email,
        service_type,
        carrier_type,
        plate_no,
        partner_id,
        account_type,
        city,
        status,
        availability,
        date_filter,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value != null) params.set(key, String(value));
      });

      if (date_filter === "range" && Array.isArray(date_value)) {
        if (date_value[0]) params.set("date_value[0]", String(date_value[0]));
        if (date_value[1]) params.set("date_value[1]", String(date_value[1]));
      } else if (
        date_filter &&
        date_value !== "" &&
        date_value != null &&
        !Array.isArray(date_value)
      ) {
        params.set("date_value", String(date_value));
      }

      const response = await axios.get(`${BASE_URL}/drivers?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// add drivers
export const addDriver = createAsyncThunk(
  "drivers/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/drivers/store`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      const data = response.data;
      if (data?.status === false) {
        return rejectWithValue(extractApiErrorMessage(data));
      }
      return data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error.response?.data));
    }
  }
);
// get drivers record
export const driverRecord = createAsyncThunk(
  "drivers/driverRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/drivers/show/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// update drivers
export const updateDriver = createAsyncThunk(
  "drivers/updateDriver",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const isFormData = typeof FormData !== "undefined" && formData instanceof FormData;
      const body = isFormData ? formData : { driver_id: id, ...formData };
      if (isFormData) {
        body.append("driver_id", String(id));
      }
      const response = await axios.post(`${BASE_URL}/drivers/update`, body, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          Lang: i18n.language,
        },
      });
      const data = response.data;
      if (data?.status === false) {
        return rejectWithValue(extractApiErrorMessage(data));
      }
      return data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error.response?.data));
    }
  }
);
// delete drivers
export const deleteDriver = createAsyncThunk(
  "drivers/deleteDriver",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/drivers/delete`,
        { driver_id: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete driver";
      return rejectWithValue({ message });
    }
  }
);
// Fetch all drivers once (for filters)
export const fetchAllDrivers = createAsyncThunk(
  "drivers/fetchAllFull",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/drivers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load all data";
      return rejectWithValue(message);
    }
  }
);

const FILTER_LIST_FIELDS = new Set([
  "name",
  "email",
  "service_type",
]);

export const fetchDriversForFilter = createAsyncThunk(
  "drivers/fetchForFilter",
  async ({ page = 1, per_page = 20, search = "", field = "name" }, { rejectWithValue }) => {
    if (field === "plate_no" || !FILTER_LIST_FIELDS.has(field)) {
      return rejectWithValue("Invalid filter field");
    }
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(per_page),
      });
      if (search) params.set("search", search);
      const response = await axios.get(`${BASE_URL}/drivers?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { field, page, search, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load filter data";
      return rejectWithValue(message);
    }
  }
);
const DriversSlice = createSlice({
  name: "drivers",
  initialState: {
    drivers: [],
    all_drivers: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    successDelete: null,
    errorDelete: null,
    pagination: null,
    filterCache: {},
    filterCacheLoading: false,
    filterCacheLoadingField: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.drivers = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(fetchAllDrivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDrivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.all_drivers = action.payload.data.data;
      })
      .addCase(fetchAllDrivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load all drivers";
      })
      .addCase(fetchDriversForFilter.pending, (state, action) => {
        state.filterCacheLoading = true;
        state.filterCacheLoadingField = action.meta.arg.field;
        const { field, search = "" } = action.meta.arg;
        const prev = state.filterCache[field] || {
          rows: [],
          pagination: null,
          search: "",
        };
        state.filterCache[field] = {
          ...prev,
          pendingSearch: search,
        };
      })
      .addCase(fetchDriversForFilter.fulfilled, (state, action) => {
        state.filterCacheLoading = false;
        state.filterCacheLoadingField = null;
        const { field, page, search = "", data } = action.payload;
        const newRows = data?.data?.data || [];
        const pagination = data?.data?.pagination || null;
        const prev = state.filterCache[field] || {
          rows: [],
          pagination: null,
          search: "",
        };
        if (page === 1 && search !== prev.pendingSearch) {
          return;
        }
        if (page > 1 && search !== prev.search) {
          return;
        }
        const mergedRows =
          page === 1
            ? newRows
            : [
                ...prev.rows,
                ...newRows.filter(
                  (row) => row?.id != null && !prev.rows.some((p) => p.id === row.id)
                ),
              ];
        state.filterCache[field] = {
          rows: mergedRows,
          pagination,
          search,
          pendingSearch: search,
        };
      })
      .addCase(fetchDriversForFilter.rejected, (state) => {
        state.filterCacheLoading = false;
        state.filterCacheLoadingField = null;
      })
      .addCase(addDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(driverRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(driverRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(driverRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updateDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteDriver.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.meta.arg;

        if (Array.isArray(state.drivers)) {
          state.drivers = state.drivers.filter(
            (record) => String(record?.id) !== String(deletedId)
          );
        }

        if (Array.isArray(state.all_drivers)) {
          state.all_drivers = state.all_drivers.filter(
            (record) => String(record?.id) !== String(deletedId)
          );
        }

        state.successDelete = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete driver";
      });
  },
});

export const { clearState } = DriversSlice.actions;
export default DriversSlice.reducer;
